import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OfflineProvider } from "./contexts/OfflineContext";
import { ShiftProvider } from "./contexts/ShiftContext";
import { PreferencesProvider } from "./contexts/PreferencesContext";
import { NegocioProvider } from "./contexts/NegocioContext";
import { TurnoBar } from "./components/TurnoBar";
import { TopNav } from "./components/TopNav";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/sonner";

// Pages
import { Login } from "./pages/Login";
import { MiJornada } from "./pages/MiJornada";
import { Asistencia } from "./pages/Asistencia";
import { InventarioInicial } from "./pages/InventarioInicial";
import { PuntoDeVenta } from "./pages/PuntoDeVenta";
import { RegistrarVenta } from "./pages/RegistrarVenta";
import { Movimiento } from "./pages/Movimiento";
import { VerInventario } from "./pages/VerInventario";
import { Corte } from "./pages/Corte";
import { Precios } from "./pages/Precios";
import { Transferencias } from "./pages/Transferencias";
import { NuevaTransferencia } from "./pages/NuevaTransferencia";
import { TrasladosPOS } from "./pages/TrasladosPOS";
import { AdminUsuarios } from "./pages/admin/AdminUsuarios";
import { AdminPrecios } from "./pages/admin/AdminPrecios";
import { AdminListasPrecios } from "./pages/admin/AdminListasPrecios";
import { AdminPuntos } from "./pages/admin/AdminPuntos";
import { AdminReportes } from "./pages/admin/AdminReportes";
import { AdminBitacora } from "./pages/admin/AdminBitacora";
import { AdminAsignaciones } from "./pages/admin/AdminAsignaciones";
import { AdminInventarioDashboard } from "./pages/admin/AdminInventarioDashboard";
import { AdminNomina } from "./pages/admin/AdminNomina";
import { MiNomina } from "./pages/MiNomina";
import { AbonosClientes } from "./pages/AbonosClientes";
import { MovimientoDinero } from "./pages/MovimientoDinero";
import { PropietarioDashboard } from "./pages/PropietarioDashboard";
import { AdminCategoriasGastos } from "./pages/admin/AdminCategoriasGastos";
import { ReporteCuentasPorCobrar } from "./pages/admin/ReporteCuentasPorCobrar";
import { AdminFacturacion } from "./pages/admin/AdminFacturacion";
import { AdminClientes } from "./pages/admin/AdminClientes";
import { AdminAlertas } from "./pages/admin/AdminAlertas";
import { AdminAutorizaciones } from "./pages/admin/AdminAutorizaciones";
import { Configuracion } from "./pages/Configuracion";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/mi-jornada" replace />;
  }

  return children;
}

// Public Route (redirect if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (user && user !== false) {
    return <Navigate to="/mi-jornada" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <>
      <TurnoBar />
      <TopNav />
      <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

      {/* Protected Routes - All Users */}
      <Route path="/mi-jornada" element={<ProtectedRoute><MiJornada /></ProtectedRoute>} />
      <Route path="/asistencia" element={<ProtectedRoute><Asistencia /></ProtectedRoute>} />
      <Route path="/inventario-inicial" element={<ProtectedRoute><InventarioInicial /></ProtectedRoute>} />
      <Route path="/pos" element={<ProtectedRoute><PuntoDeVenta /></ProtectedRoute>} />
      <Route path="/registrar-venta" element={<ProtectedRoute><RegistrarVenta /></ProtectedRoute>} />
      <Route path="/movimiento" element={<ProtectedRoute><Movimiento /></ProtectedRoute>} />
      <Route path="/movimiento-dinero" element={<ProtectedRoute><MovimientoDinero /></ProtectedRoute>} />
      <Route path="/ver-inventario" element={<ProtectedRoute><VerInventario /></ProtectedRoute>} />
      <Route path="/corte" element={<ProtectedRoute><Corte /></ProtectedRoute>} />
      <Route path="/precios" element={<ProtectedRoute><Precios /></ProtectedRoute>} />

      {/* Protected Routes - Repartidor & Vendedor */}
      <Route path="/transferencias" element={<ProtectedRoute><Transferencias /></ProtectedRoute>} />
      <Route path="/transferencias/nueva" element={<ProtectedRoute allowedRoles={['repartidor', 'admin']}><NuevaTransferencia /></ProtectedRoute>} />
      <Route path="/traslados-pos" element={<ProtectedRoute><TrasladosPOS /></ProtectedRoute>} />

      {/* Admin + Propietario Routes */}
      <Route path="/admin/usuarios" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminUsuarios /></ProtectedRoute>} />
      <Route path="/admin/precios" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminPrecios /></ProtectedRoute>} />
      <Route path="/admin/listas-precios" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminListasPrecios /></ProtectedRoute>} />
      <Route path="/admin/puntos" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminPuntos /></ProtectedRoute>} />
      <Route path="/admin/reportes" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminReportes /></ProtectedRoute>} />
      <Route path="/admin/bitacora" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminBitacora /></ProtectedRoute>} />
      <Route path="/admin/alertas" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminAlertas /></ProtectedRoute>} />
      <Route path="/admin/autorizaciones" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminAutorizaciones /></ProtectedRoute>} />
      <Route path="/admin/asignaciones" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminAsignaciones /></ProtectedRoute>} />
      <Route path="/admin/inventario" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminInventarioDashboard /></ProtectedRoute>} />
      <Route path="/admin/nomina" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminNomina /></ProtectedRoute>} />
      <Route path="/propietario/dashboard" element={<ProtectedRoute allowedRoles={['propietario', 'admin']}><PropietarioDashboard /></ProtectedRoute>} />
      <Route path="/admin/categorias-gastos" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminCategoriasGastos /></ProtectedRoute>} />
      <Route path="/admin/cuentas-por-cobrar" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><ReporteCuentasPorCobrar /></ProtectedRoute>} />
      <Route path="/admin/facturacion" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminFacturacion /></ProtectedRoute>} />
      <Route path="/admin/clientes" element={<ProtectedRoute allowedRoles={['admin', 'propietario']}><AdminClientes /></ProtectedRoute>} />
      <Route path="/mi-nomina" element={<ProtectedRoute><MiNomina /></ProtectedRoute>} />
      <Route path="/abonos-clientes" element={<ProtectedRoute><AbonosClientes /></ProtectedRoute>} />
      <Route path="/configuracion" element={<ProtectedRoute><Configuracion /></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/mi-jornada" replace />} />
      <Route path="/dashboard" element={<Navigate to="/mi-jornada" replace />} />
      <Route path="*" element={<Navigate to="/mi-jornada" replace />} />
    </Routes>
    <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <PreferencesProvider>
        <NegocioProvider>
          <AuthProvider>
            <OfflineProvider>
              <BrowserRouter>
                <ShiftProvider>
                  <AppRoutes />
                </ShiftProvider>
              </BrowserRouter>
              <Toaster position="top-center" richColors />
            </OfflineProvider>
          </AuthProvider>
        </NegocioProvider>
      </PreferencesProvider>
    </div>
  );
}

export default App;
